import { eAccessRights } from "functions/api/enums";
import { createContext, useContext } from "react";
import { defaultSettings } from "./appsettings/defaultSettings";
import {
  IAppSettings,
  IAppSettingsContextValue,
  IUnifiedAction,
  IUnifiedState,
} from "./appsettings/interface";
import { hierarchyInitialState } from "./hierarchy/hierarchyInitialState";
import { IHierarchyStatusContext } from "./hierarchy/interface";
import { tlfInitialState } from "./tlf/tlfContextInitialState";
import { tocInitialState } from "./toc/initialTocState";
import { TocContextProps } from "./toc/interface";

/**
 * The default unified state used to initialise the context before the provider mounts.
 * App-settings fields come from {@link defaultSettings}; hierarchy and ToC slices are
 * set to their respective initial states.
 */
export const unifiedInitialState: IUnifiedState = {
  ...defaultSettings,
  clinicalLibraries: {},
  isClinicalLibraryLoading: false,
  saveRequest: { requesting: false, error: false },
  undoHistory: [],
  undoIndex: 0,
  hierarchyState: hierarchyInitialState,
  tocState: tocInitialState,
  tlfState: tlfInitialState,
};

/**
 * AppSettingsContext following the ARS-style `{ state, dispatch }` pattern.
 * Provides the unified application state — encompassing app settings, hierarchy,
 * and ToC slices — along with a single dispatch function for all unified operations.
 */
export const AppSettingsContext = createContext<IAppSettingsContextValue>({
  state: unifiedInitialState,
  dispatch:
    // istanbul ignore next
    () => {},
});

/**
 * Hook to access the application settings portion of the unified context state.
 * Returns an {@link IAppSettings}-compatible view of the unified state, preserving
 * backward compatibility for existing consumers that previously used
 * `useContext(AppSettingsContext)` directly.
 *
 * @returns The current application settings extracted from the unified state.
 */
export const useAppSettings = (): IUnifiedState & {
  dispatch: React.Dispatch<IUnifiedAction>;
} => {
  const { state, dispatch } = useContext(AppSettingsContext);
  return { ...state, dispatch };
};

/**
 * Convenience hook replacing the former standalone `HierarchyContext`.
 * Sources hierarchy state and dispatch directly from the unified {@link AppSettingsContext},
 * deriving the page title the same way {@link useHierarchyEffects} does. Returns `null` when no
 * {@link AppSettingsContext.Provider} is mounted (i.e. the raw default context value is in
 * effect), matching the previous `HierarchyContext`'s nullable default for consumers rendered
 * outside a hierarchy-aware route.
 *
 * @returns The current hierarchy state, dispatch, and derived title, or `null` when unprovided.
 */
export const useHierarchyContext = (): IHierarchyStatusContext | null => {
  const { state, dispatch } = useContext(AppSettingsContext);
  if (state.hierarchyState === hierarchyInitialState) return null;
  const hierarchyState = state.hierarchyState;
  const title = hierarchyState.hierarchy
    ? `${hierarchyState.hierarchy.name}`
    : `Loading hierarchy... ${hierarchyState.id}`;
  return { ...hierarchyState, dispatch, title };
};

/**
 * Convenience hook replacing the former standalone `TocContext`.
 * Sources ToC state and dispatch directly from the unified {@link AppSettingsContext}.
 * Returns `null` when no {@link AppSettingsContext.Provider} is mounted (i.e. the raw default
 * context value is in effect), matching the previous `TocContext`'s nullable default for consumers
 * rendered outside a hierarchy-aware route.
 *
 * @returns The current ToC state and dispatch, or `null` when unprovided.
 */
export const useTocContext = (): TocContextProps => {
  const { state, dispatch } = useContext(AppSettingsContext);
  return {
    state: {
      ...state.tocState,
      readOnly:
        state.hierarchyState.hierarchy?.isBackupVersion ||
        !state.hierarchyState.accessRights.includes(eAccessRights.WriteToTlfItemsInHierarchy),
    },
    dispatch,
    tocRequest: state.tocState.tocRequestStatus,
  };
};

export { useTlfContext } from "./tlf/useTlfContext";
